// client/src/lib/support/listenerRetry.ts
import type { DocumentData, DocumentSnapshot, QuerySnapshot } from 'firebase/firestore';

/**
 * Keep a Firestore live listener alive across a transient stall (Sentry OFFICEHUB-CLIENT-2F).
 *
 * WHY: a snapshot listener that fails is DEAD: Firestore delivers the error once and sends nothing more. The support
 * inbox retried only a permission error; on `deadline-exceeded` it showed "Couldn't load calls - some inbox items may be
 * missing" and the channel stayed frozen until the person refreshed the page (6 users, 14 events in 87 days, mostly the
 * inbox). A timeout says the stream stalled, not that the data is forbidden, so asking again usually works.
 *
 * WHAT: re-subscribe the failed listener after a growing delay, at most `delaysMs.length` times in a row (the counter
 * resets the moment a snapshot arrives, so a recovered stream gets its full allowance again). When the allowance is used
 * up, or the error is not transient, the caller's `onError` runs exactly as before. Teardown cancels the pending timer
 * and the live listener, so a retry can never outlive the screen.
 *
 * NOT retried on purpose: permission-denied / unauthenticated (the hook has its own token-refresh path), and
 * resource-exhausted (a quota error: retrying makes it worse).
 */

/** Listener error codes that mean "the stream stalled or the service blinked", never "you may not read this". */
export const TRANSIENT_LISTENER_CODES: ReadonlySet<string> = new Set(['deadline-exceeded', 'unavailable', 'aborted']);

/** Waits before retry 1, 2, 3. Three tries, then the caller's error path. */
export const LISTENER_RETRY_DELAYS_MS: readonly number[] = [2_000, 8_000, 30_000];

/**
 * Is this a transient Firestore listener error?
 * @param {unknown} err what the listener's error callback received
 * @return {boolean} true for deadline-exceeded / unavailable / aborted
 */
export function isTransientListenerError(err: unknown): boolean {
  const code = String((err as { code?: unknown } | null)?.code ?? '').replace(/^firestore\//, '');
  return TRANSIENT_LISTENER_CODES.has(code);
}

/** Snapshot types the Firestore listeners hand to `onData`, so call sites can name T without another firebase import. */
export type QuerySnap = QuerySnapshot<DocumentData>;
export type DocSnap = DocumentSnapshot<DocumentData>;

export interface RetryingSubscriptionOptions<T> {
  /** Start one listener. Called again for every retry. Returns its unsubscribe. */
  subscribe: (onData: (value: T) => void, onError: (err: Error) => void) => () => void;
  /** Every snapshot, from whichever listener instance is live. */
  onData: (value: T) => void;
  /** A non-transient error, or a transient one after the allowance is spent. */
  onError: (err: Error) => void;
  /** Naming for the log line. */
  label: string;
  /** Test hook: waits between retries. */
  delaysMs?: readonly number[];
}

/**
 * Subscribe, and quietly re-subscribe after a transient listener error.
 * @param {RetryingSubscriptionOptions<T>} opts how to subscribe and what to do with data and final errors
 * @return {() => void} unsubscribe: cancels any pending retry and the live listener
 */
export function subscribeWithTransientRetry<T>(opts: RetryingSubscriptionOptions<T>): () => void {
  const delays = opts.delaysMs ?? LISTENER_RETRY_DELAYS_MS;
  let stopped = false;
  let attempt = 0;
  let generation = 0; // which listener instance is live; a late callback from an old one is ignored
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unsubscribe: (() => void) | undefined;

  const stopListener = () => {
    const fn = unsubscribe;
    unsubscribe = undefined;
    fn?.();
  };

  const start = () => {
    if (stopped) return;
    timer = undefined;
    generation += 1;
    const gen = generation;
    const next = opts.subscribe(
      (value) => {
        if (stopped || gen !== generation) return;
        attempt = 0; // a snapshot arrived: the stream is healthy again
        opts.onData(value);
      },
      (err) => {
        if (stopped || gen !== generation) return;
        if (isTransientListenerError(err) && attempt < delays.length) {
          const wait = delays[attempt];
          attempt += 1;
          // The failed listener is dead already; release it before asking again so nothing is left subscribed, and
          // retire its generation so a late callback from it cannot schedule a second retry.
          generation += 1;
          stopListener();
          // eslint-disable-next-line no-console
          console.warn(`[listenerRetry] ${opts.label}: transient listener error, re-subscribing in ${wait} ms (try ${attempt}/${delays.length})`, err);
          timer = setTimeout(start, wait);
          return;
        }
        opts.onError(err);
      },
    );
    unsubscribe = next;
    // An error delivered synchronously inside subscribe() already scheduled a retry; the listener it came from is dead.
    if (gen !== generation || timer !== undefined) stopListener();
  };

  start();

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    stopListener();
  };
}
